import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Alert
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { listDatabaseColumns, listDatabaseTables } from '../../services/databaseSchema';

export function EventNodeFields({ localNodeData, updateNodeData }) {
    const triggerType = localNodeData.triggerType || '';

    // Legacy / compat: params sigue existiendo y se usa para otros triggerType.
    const params = localNodeData.params || [];

    // Estado UI para el modo database
    const [dbTables, setDbTables] = useState([]);
    const [dbColumns, setDbColumns] = useState([]);
    const [dbError, setDbError] = useState('');

    const dbConfig = localNodeData.database || {};
    const selectedTable = dbConfig.table || '';
    const mappings = dbConfig.mappings || [];

    const excludedColumns = useMemo(() => {
      // heurística: ids y trazabilidad comunes (camelCase / snake_case)
      return new Set([
        'id',
        '_id',
        'createdat',
        'created_at',
        'createddate',
        'created_date',
        'updatedat',
        'updated_at',
        'updateddate',
        'updated_date',
        'deletedat',
        'deleted_at',
        'deleteddate',
        'deleted_date',
        'createdby',
        'created_by',
        'updatedby',
        'updated_by',
        'deletedby',
        'deleted_by',
        'traceid',
        'trace_id',
        'trazabilidad',
        'fecha_creacion',
        'fecha_modificacion'
      ]);
    }, []);

    const normalizeColumnKey = (c) => (c || '').toString().trim().toLowerCase();

    const isColumnExcluded = (col) => {
      const k = normalizeColumnKey(col);
      if (!k) return true;
      if (excludedColumns.has(k)) return true;
      // excluir por prefijos/sufijos comunes
      if (k === 'uuid' || k.endsWith('_uuid')) return true;
      if (k.endsWith('_id') && k !== 'client_id') return true; // heurística: permitir client_id si aplica
      return false;
    };

    const toBackendParamValue = (m) => {
      if (!m) return '';
      if (m.sourceType === 'var') {
        const key = (m.varKey || '').trim();
        if (!key) return '';
        // guardamos con {{}} para que sea consistente con otros nodos (APICall placeholder)
        return key.startsWith('{{') ? key : `{{${key}}}`;
      }
      return (m.literalValue ?? '').toString();
    };

    const syncLegacyParamsFromMappings = (nextMappings) => {
      // Mantener compatibilidad: params: [{name, value}]
      const nextParams = (nextMappings || []).map((m) => ({
        name: m.column,
        value: toBackendParamValue(m)
      }));
      updateNodeData('params', nextParams);
    };

    const updateDbConfig = (patch) => {
      updateNodeData('database', {
        ...dbConfig,
        ...patch
      });
    };

    const handleAddParam = () => {
        const newParams = [...params, { name: '', value: '' }];
        updateNodeData('params', newParams);
    };

    const handleChangeParam = (index, field, value) => {
        const newParams = params.map((p, i) =>
            i === index ? { ...p, [field]: value } : p
        );
        updateNodeData('params', newParams);
    };

    const handleDeleteParam = (index) => {
        const newParams = params.filter((_, i) => i !== index);
        updateNodeData('params', newParams);
    };

    // Cargar tablas al entrar en modo database
    useEffect(() => {
      let mounted = true;
      const load = async () => {
        if (triggerType !== 'database') return;
        setDbError('');
        try {
          const tables = await listDatabaseTables();
          if (!mounted) return;
          setDbTables(tables);

          if (!tables.length) {
            setDbError('No se pudieron cargar tablas desde el backend (endpoint de schema no disponible o sin fallback).');
          }
        } catch {
          if (!mounted) return;
          setDbError('Error al cargar tablas.');
        }
      };
      load();
      return () => {
        mounted = false;
      };
    }, [triggerType]);

    // Cargar columnas al seleccionar tabla
    useEffect(() => {
      let mounted = true;
      const load = async () => {
        if (triggerType !== 'database') return;
        if (!selectedTable) {
          setDbColumns([]);
          return;
        }
        setDbError('');
        try {
          const cols = await listDatabaseColumns(selectedTable);
          if (!mounted) return;
          setDbColumns(cols);

          if (!cols.length) {
            setDbError('No se pudieron cargar columnas para la tabla seleccionada.');
          }
        } catch {
          if (!mounted) return;
          setDbError('Error al cargar columnas.');
        }
      };
      load();
      return () => {
        mounted = false;
      };
    }, [triggerType, selectedTable]);

    // Autogenerar/merge mappings al obtener columnas
    useEffect(() => {
      if (triggerType !== 'database') return;
      if (!selectedTable) return;
      if (!dbColumns.length) return;

      const allowedColumns = dbColumns.filter((c) => !isColumnExcluded(c));

      const byColumn = new Map(mappings.map((m) => [m.column, m]));
      const nextMappings = allowedColumns.map((col) => {
        const existing = byColumn.get(col);
        return (
          existing || {
            column: col,
            sourceType: 'var',
            varKey: '',
            literalValue: ''
          }
        );
      });

      // Evitar update si no cambió (compare simple por JSON)
      const prevJson = JSON.stringify(mappings);
      const nextJson = JSON.stringify(nextMappings);
      if (prevJson !== nextJson) {
        updateDbConfig({ mappings: nextMappings });
        syncLegacyParamsFromMappings(nextMappings);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [triggerType, selectedTable, dbColumns]);

    const handleTableChange = (table) => {
      updateDbConfig({ table, mappings: [] });
      updateNodeData('params', []);
    };

    const handleMappingChange = (index, patch) => {
      const nextMappings = mappings.map((m, i) => (i === index ? { ...m, ...patch } : m));
      updateDbConfig({ mappings: nextMappings });
      syncLegacyParamsFromMappings(nextMappings);
    };

    const renderDatabaseFields = () => (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Base de Datos
        </Typography>

        {dbError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {dbError}
          </Alert>
        ) : null}

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <Select
            variant="outlined"
            value={selectedTable}
            displayEmpty
            onChange={(e) => handleTableChange(e.target.value)}
          >
            <MenuItem value="">
              <em>Selecciona una tabla</em>
            </MenuItem>
            {dbTables.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTable ? (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Se autogeneran parámetros por columna (sin id ni trazabilidad). Puedes cambiar la fuente: variable del flujo o valor fijo.
            </Typography>

            {mappings.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay columnas mapeables para esta tabla.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {mappings.map((m, index) => (
                  <Box
                    key={m.column || index}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center'
                    }}
                  >
                    <TextField
                      size="small"
                      label="Columna"
                      value={m.column || ''}
                      disabled
                      sx={{ width: 220 }}
                    />

                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={m.sourceType || 'var'}
                      onChange={(_, next) => {
                        if (!next) return;
                        handleMappingChange(index, {
                          sourceType: next,
                          // limpiar el otro campo para evitar ambigüedad
                          varKey: next === 'var' ? (m.varKey || '') : '',
                          literalValue: next === 'literal' ? (m.literalValue || '') : ''
                        });
                      }}
                    >
                      <ToggleButton value="var">Var</ToggleButton>
                      <ToggleButton value="literal">Fijo</ToggleButton>
                    </ToggleButtonGroup>

                    {m.sourceType === 'literal' ? (
                      <TextField
                        size="small"
                        fullWidth
                        label="Valor fijo"
                        value={m.literalValue ?? ''}
                        onChange={(e) => handleMappingChange(index, { literalValue: e.target.value })}
                      />
                    ) : (
                      <TextField
                        size="small"
                        fullWidth
                        label="Variable"
                        placeholder="Ej: rut_cliente (o {{rut_cliente}})"
                        value={m.varKey ?? ''}
                        onChange={(e) => handleMappingChange(index, { varKey: e.target.value })}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </>
        ) : null}
      </Box>
    );

    return (
        <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                Tipo Evento
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <Select
                    variant="outlined"
                    value={triggerType}
                    onChange={(e) => updateNodeData('triggerType', e.target.value)}
                >
                    <MenuItem value="hubspot">Inyección a Hubspot</MenuItem>
                    <MenuItem value="database">Inyección Base de Datos</MenuItem>
                    <MenuItem value="wolkvox">Inyección a Wolkvox</MenuItem>
                    <MenuItem value="email">Envío de Email</MenuItem>
                </Select>
            </FormControl>

            {triggerType === 'database' ? (
              renderDatabaseFields()
            ) : (
              <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      Parámetros
                  </Typography>

                  {params.map((param, index) => (
                      <Box
                          key={index}
                          sx={{
                              display: 'flex',
                              gap: 1,
                              mb: 1,
                              alignItems: 'center'
                          }}
                      >
                          <TextField
                              size="small"
                              fullWidth
                              label="Nombre del parámetro"
                              value={param.name}
                              onChange={(e) => handleChangeParam(index, 'name', e.target.value)}
                          />
                          <TextField
                              size="small"
                              fullWidth
                              label="Nodo"
                              value={param.value}
                              onChange={(e) => handleChangeParam(index, 'value', e.target.value)}
                          />
                          <IconButton
                              size="small"
                              onClick={() => handleDeleteParam(index)}
                          >
                              <DeleteIcon fontSize="small" />
                          </IconButton>
                      </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <IconButton onClick={handleAddParam} size="small">
                      <AddCircleOutlineIcon fontSize="small" />
                      <Typography variant="caption" sx={{ ml: 0.5 }}>
                        Agregar
                      </Typography>
                    </IconButton>
                  </Box>
              </Box>
            )}
        </Box>
    );
}
